import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { Textarea } from 'components/ui/textarea'
import { Button } from 'components/ui/button'
import { FolderOpen } from 'lucide-react'
import { fetchLookupLists } from 'slices/lookupListSlice'

const initialValues = {
	id: 0,
	user: '',
	assetName: '',
	description: '',
	portfolioType: '',
}

interface PortfolioAddNewCNProps {
	portfolioAdd: (values: typeof initialValues, resetForm: () => void) => void
}

const PortfolioAddNewCN = ({ portfolioAdd }: PortfolioAddNewCNProps) => {
	const dispatch = useDispatch()
	const { entities: lookupEntities, ids: lookupIds, isInitialized: lookupLoaded } = useSelector((state: any) => state.lookupLists)

	useEffect(() => {
		if (!lookupLoaded) dispatch(fetchLookupLists(undefined) as any)
	}, [dispatch, lookupLoaded])

	const portfolioTypeOptions = useMemo(
		() =>
			lookupIds
				.map((id: string) => lookupEntities[id])
				.filter((item: any) => item && item.listType === 'portfolio')
				.map((item: any) => item.option)
				.sort((a: string, b: string) => a.localeCompare(b)),
		[lookupEntities, lookupIds],
	)

	const [values, setValues] = useState({ ...initialValues })
	const [errors, setErrors] = useState<Record<string, string>>({})

	const resetForm = () => {
		setValues({ ...initialValues })
		setErrors({})
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target
		setValues(prev => ({ ...prev, [name]: value }))
	}

	const validate = () => {
		const temp: Record<string, string> = {}
		temp.assetName = values.assetName ? '' : '*Required field'
		temp.description = values.description ? '' : '*Required field'
		temp.portfolioType = values.portfolioType ? '' : '*Required field'
		setErrors(temp)
		return Object.values(temp).every(x => x === '')
	}

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (validate()) {
			portfolioAdd(values, resetForm)
		}
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div>
				<Label htmlFor="assetName" className="mb-1.5 block">Name</Label>
				<Input
					id="assetName"
					name="assetName"
					value={values.assetName}
					onChange={handleChange}
				/>
				{errors.assetName && <p className="mt-1 text-xs text-red-500">{errors.assetName}</p>}
			</div>

			<div>
				<Label htmlFor="portfolioType" className="mb-1.5 block">Portfolio Type</Label>
				<Select
					id="portfolioType"
					name="portfolioType"
					value={values.portfolioType}
					onChange={handleChange}
				>
					<option value="">Select type...</option>
					{portfolioTypeOptions.map((type: string) => (
						<option key={type} value={type}>{type}</option>
					))}
				</Select>
				{errors.portfolioType && <p className="mt-1 text-xs text-red-500">{errors.portfolioType}</p>}
			</div>

			<div>
				<Label htmlFor="description" className="mb-1.5 block">Description / Note</Label>
				<Textarea
					id="description"
					name="description"
					value={values.description}
					onChange={handleChange}
					rows={3}
				/>
				{errors.description && <p className="mt-1 text-xs text-red-500">{errors.description}</p>}
			</div>

			{/* Everything below the inputs is centred, and the submit button
			    names the asset and carries the popup's own icon. */}
			<div className="flex items-center justify-center gap-3 pt-2">
				<Button type="button" variant="ghost" onClick={resetForm}>
					Clear
				</Button>
				<Button type="submit" variant="primary" className="gap-2">
					<FolderOpen className="w-4 h-4" />
					Add Portfolio
				</Button>
			</div>
		</form>
	)
}

export default PortfolioAddNewCN
